import { useForm } from 'react-hook-form'
import { useCreateBookingRequest } from '@cocoonspace/sdk-js/v2/domains/schedulers/schedulers'
import { Card } from '@cocoonspace/ui/components/card'
import { Form } from '@cocoonspace/ui/components/form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useLingui } from '@lingui/react/macro'
import { isAxiosError } from 'axios'
import { useRouter } from 'next/navigation'
import { toast } from 'sonner'
import * as z from 'zod'

import { useIsRouterLoading } from '~/hooks/use-is-router-loading.hook'
import { DatePicker } from './date_picker'
import { EventRecap } from './event_recap'
import { NotFound } from './not-found'
import { AnswerCardProvider, useAnswerCard } from './provider'
import { TimePicker } from './time_picker'
import { useCachedEventTypeAvailabilities } from './use-cached-event-type-availabilities.hook'
import { Validation } from './validation'

export type BookingRequestAnswerFormData = z.infer<typeof formSchema>

const formSchema = z.object({
	step: z.enum(['dateTime', 'validation']),
	title: z.string().min(1),
	day: z.string().min(1),
	start: z.string().min(1),
	email: z.email().min(1),
	requestedLocation: z.object({
		type: z.enum(['cocoon', 'custom']),
		space_id: z.string().min(1),
	}),
	additionalInfos: z.object({
		first_name: z.string().min(1),
		last_name: z.string().min(1),
		comment: z.string().optional(),
	}),
})

const StepDateTime = () => {
	const { eventType, currentMonth } = useAnswerCard()

	const { data: monthSlots, isLoading } = useCachedEventTypeAvailabilities(
		eventType.id,
		{
			month: (currentMonth.getMonth() + 1).toString(),
			year: currentMonth.getFullYear().toString(),
		},
	)

	return (
		<Card className='flex w-full flex-col gap-0 divide-y overflow-hidden p-0 md:flex-row md:divide-x md:divide-y-0'>
			<EventRecap />
			<DatePicker
				monthSlots={monthSlots}
				isLoading={isLoading}
			/>
			<TimePicker monthSlots={monthSlots} />
		</Card>
	)
}

const StepValidation = () => (
	<Card className='flex w-full flex-col gap-0 divide-y overflow-hidden p-0 md:flex-row md:divide-x md:divide-y-0'>
		<EventRecap />
		<Validation />
	</Card>
)

export const AnswerCardContent = ({ idOrSlug }: { idOrSlug: string }) => {
	const router = useRouter()
	const isRouterLoading = useIsRouterLoading()
	const { step, eventType } = useAnswerCard()
	const { t } = useLingui()

	const createBookingRequestMutation = useCreateBookingRequest()

	const form = useForm<BookingRequestAnswerFormData>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			step: 'dateTime',
			title: eventType.title,
			day: '',
			start: '',
			email: '',
			requestedLocation: {
				type: eventType.location?.type as any,
				space_id: '',
			},
			additionalInfos: {
				comment: undefined,
			},
		},
	})

	// oxlint-disable-next-line no-unused-vars
	const onSubmit = ({ step, ...data }: BookingRequestAnswerFormData) => {
		return createBookingRequestMutation
			.mutateAsync({
				eventTypeId: idOrSlug,
				data,
			})
			.then((br) => {
				router.push(`/scheduling/success/${br.id}`)
			})
			.catch((err) => {
				if (isAxiosError(err) && err.status === 409) {
					return toast.error(t`Il n'y a pas de disponibilité pour cette date`)
				}
				return toast.error(t`Une erreur est survenue`)
			})
	}

	if (!eventType.enabled) return <NotFound />

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)}>
				<fieldset
					disabled={createBookingRequestMutation.isPending || isRouterLoading}
					className='flex flex-1 flex-col items-center justify-center gap-2'
				>
					{step === 'dateTime' && <StepDateTime />}
					{step === 'validation' && <StepValidation />}
				</fieldset>
			</form>
		</Form>
	)
}

export const AnswerCard = ({ idOrSlug }: { idOrSlug: string }) => (
	<AnswerCardProvider idOrSlug={idOrSlug}>
		<AnswerCardContent idOrSlug={idOrSlug} />
	</AnswerCardProvider>
)
